import type { ISupplier } from '@bridebook/models/source/models/Weddings/Suppliers.types';
import { useBudgetSlice } from '@bridebook/web/lib/ai-planner/hooks/use-budget-slice';
import { useCoachStateQuery } from '@bridebook/web/lib/ai-planner/hooks/use-coach-state';
import { useCountryCostsQuery } from '@bridebook/web/lib/ai-planner/hooks/use-country-costs';
import { resolveBudget } from '@bridebook/web/lib/ai-planner/planning/budget/resolve-budget';
import type { BudgetProjection } from '@bridebook/web/lib/ai-planner/planning/budget/types';
import { useSelector } from '@bridebook/web/lib/utils';
import {
  getProfile,
  getWeddingId,
  isBrideOnlyWedding,
  isGroomOnlyWedding,
} from '@bridebook/web/lib/weddings/selectors';
import { useMemo } from 'react';

/**
 * The couple's budget breakdown, real or projected from the country template. Null while
 * the budget slice or the template is still loading. Starts the budget slice itself, so
 * any surface that shows a budget gets one on any route. Settled once every source it
 * reads has finished its first load, whatever the outcome; failed once one of them came
 * back empty-handed and will not settle on its own.
 */
export const useBudgetProjection = (): {
  projection: BudgetProjection | null;
  settled: boolean;
  failed: boolean;
} => {
  const wedding = useSelector(getProfile);
  const weddingId = useSelector(getWeddingId) || undefined;
  const costs = useSelector((state) => state.budget.costs);
  // Enough to resolve names for a real budget; a projection needs `targets`,
  // which this slice is fetched without.
  const costsInitial = useSelector((state) => state.budget.costsInitial);
  const budgetLoaded = useSelector((state) => state.budget.listenerInitialised);
  const budgetFailed = useSelector((state) => state.budget.listenerFailed);
  const groomOnly = useSelector(isGroomOnlyWedding);
  const brideOnly = useSelector(isBrideOnlyWedding);
  const suppliers = useSelector((state) => state.shortlist.list);
  const { failed: epicsFailed } = useBudgetSlice(weddingId);

  const hasOwnCosts = Object.keys(costs ?? {}).length > 0;
  const {
    coachState: { budgetExcludedCategories },
    settled: coachSettled,
    failed: coachFailed,
  } = useCoachStateQuery(weddingId);
  // `state.budget.costsInitial` is fetched without `targets`, so the allocation
  // percentages have to come from a planner-scoped read.
  const {
    costs: countryCosts,
    settled: countryCostsSettled,
    failed: countryCostsFailed,
  } = useCountryCostsQuery(wedding?.l10n?.country, budgetLoaded && !hasOwnCosts);

  const projection = useMemo(
    () =>
      resolveBudget({
        costsInitial: hasOwnCosts ? (costsInitial ?? {}) : countryCosts,
        weddingCosts: costs,
        budgetLoaded,
        // The app's budget maths declares the model supplier and reads `type` and `booked`;
        // the shortlist slice holds the UI supplier, whose `createdAt` is a number.
        shortlist: suppliers as unknown as Record<string, ISupplier>,
        date: wedding?.date,
        country: wedding?.l10n?.country,
        guestEstimate: wedding?.guests?.estimate,
        budget: wedding?.budget,
        isGroomOnly: groomOnly,
        isBrideOnly: brideOnly,
        overrides: { excludeCategories: budgetExcludedCategories },
      }),
    [
      hasOwnCosts,
      costsInitial,
      countryCosts,
      costs,
      budgetLoaded,
      suppliers,
      wedding,
      groomOnly,
      brideOnly,
      budgetExcludedCategories,
    ],
  );
  const settled = budgetLoaded && coachSettled && countryCostsSettled;
  const failed = budgetFailed || epicsFailed || coachFailed || countryCostsFailed;

  return useMemo(() => ({ projection, settled, failed }), [projection, settled, failed]);
};
